import React, { useEffect, useState } from 'react'

import { useGlobalContext } from 'contexts/global'
import { UIButton, UIFlex, UISpacing, UIText } from 'components'
import { useLocation } from 'react-router-dom'
import { postResendOTP, postValidateOTP } from 'services/otp'
import { useAuthorizationContext } from 'contexts'
import { TConsentFinishType } from 'pages/Login/Consent/types'
import { SuccessAuth } from 'pages/SuccessAuth'
import { ErrorBottomsheet } from 'components/ErrorBottomsheet'
import { InputToken } from 'components/InputToken'
import { sendSelectContent, sendScreenView, sendError } from 'utils'
import { TError } from 'types'

import { ResendBottomsheet } from '../../components/ResendBotttomsheet'
import { TOtpState } from './types'

const TOKEN_RESEND_TIME = 4
const TOKEN_SIZE = 6

export const Token = () => {
  const { state } = useLocation()

  const { authorizationId, setRedirectUri } = useAuthorizationContext()
  const { setHeaderPage, headerPage } = useGlobalContext()
  const [token, setToken] = useState<string>('')
  const [counter, setCounter] = useState<number>(TOKEN_RESEND_TIME)
  const [loading, setLoading] = useState<boolean>(false)
  const [otpState, setOtpState] = useState<TOtpState>(state)
  const [showResendBottomsheet, setShowResendBottomsheet] = useState<boolean>(false)
  const [showErrorBottomsheet, setShowErrorBottomsheet] = useState<boolean>(false)
  const [consentFinish, setConsentFinish] = useState<TConsentFinishType>(undefined)
  const [erroValidateToken, setErroValidateToken] = useState<boolean>(false)
  const [errorData, setErrorData] = useState<TError>()

  useEffect(() => {
    setHeaderPage(undefined)
    if (!headerPage) sendScreenView({ scenario: state.client, step: 'get_token' })
  }, [authorizationId, headerPage])

  useEffect(() => {
    if (authorizationId && token.length === TOKEN_SIZE) {
      setLoading(true)
      postValidateOTP({ authorizationId, token })
        .then((data) => {
          setRedirectUri(data.redirectUri)
          setConsentFinish('successAuth')
        })
        .catch((error) => {
          setErroValidateToken(true)
          setErrorData({
            scenario: state.client,
            step: 'invalid_token',
            error_description: JSON.stringify(error),
            error_message: JSON.stringify('Código de verificação inválido'),
          })
        })
        .finally(() => setLoading(false))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, authorizationId])

  useEffect(() => {
    counter > 0 && setTimeout(() => setCounter(counter - 1), 1000)
  }, [counter])

  const descriptionByChannel = () => {
    switch (otpState.otpChannel) {
      case 'EMAIL':
        return `Insira o código que enviamos no e-mail ${otpState.otpDestination}`
      case 'WHATSAPP':
        return (
          <>
            Digite o código enviado por{' '}
            <UIText variant="body-3" bold>
              Whatsapp
            </UIText>
            . Podemos tentar por{' '}
            <UIText variant="body-3" bold>
              SMS
            </UIText>{' '}
            se precisar.
          </>
        )
      case 'SMS':
        return 'Digite o código enviado por SMS.'
      default:
        return 'Digite o código enviado.'
    }
  }

  const titleByChannel = () => {
    switch (otpState.otpChannel) {
      case 'EMAIL':
        return `Enviamos um código para o seu e-mail`
      case 'WHATSAPP':
        return `Enviamos um código para ${otpState.otpDestination}`
      case 'SMS':
        return `Enviamos um código para ${otpState.otpDestination}`
      default:
        return `Código de validação`
    }
  }

  const resendToken = (channel: string) => {
    sendSelectContent({
      scenario: state.client,
      step: 'get_token',
      element_name: 'Confirmar',
      element_type: 'button',
      parent_name: 'token',
    })

    setShowResendBottomsheet(false)
    setShowErrorBottomsheet(false)
    setErroValidateToken(false)
    setToken('')

    if (authorizationId) {
      setLoading(true)
      postResendOTP({ authorizationId, channel })
        .then((data) => {
          setOtpState(data)
          setCounter(TOKEN_RESEND_TIME)
        })
        .catch((error) => {
          setShowErrorBottomsheet(true)
          setErroValidateToken(true)
          setErrorData({
            scenario: state.client,
            step: 'resend_token_fail',
            error_description: JSON.stringify(error),
            error_message: JSON.stringify(
              'Não foi possível completar a requisição. Tente novamente mais tarde.',
            ),
          })
        })
        .finally(() => setLoading(false))
    }
  }

  useEffect(() => {
    if (errorData) sendError(errorData)
  }, [errorData])

  if (consentFinish === 'successAuth') {
    return <SuccessAuth />
  }

  return (
    <div>
      <UIText variant="headline-h1" semiBold>
        {titleByChannel()}
      </UIText>
      <UISpacing mb="sm" />
      <UIText variant="body-3">{descriptionByChannel()}</UIText>
      <UISpacing mb="xl" />

      <InputToken
        size={TOKEN_SIZE}
        value={token}
        error={erroValidateToken}
        onChange={(e) => {
          setErroValidateToken(false)
          setToken(e.target.value)
        }}
      />

      <UISpacing mb="md" />

      <UIFlex alignItems="center" justifyContent="center">
        <UIButton
          variant="secondary"
          isLoading={loading}
          disabled={counter > 0}
          onClick={() => setShowResendBottomsheet(true)}
          style={{ zIndex: 100 }}
        >
          Reenviar código {counter > 0 ? `${counter}s` : null}
        </UIButton>
      </UIFlex>
      {showResendBottomsheet && (
        <ResendBottomsheet
          resendOptions={otpState.otpResendOptions}
          onConfirm={(channel) => resendToken(channel)}
          onClose={() => setShowResendBottomsheet(false)}
        />
      )}
      {showErrorBottomsheet && (
        <ErrorBottomsheet
          title="Ocorreu um erro"
          description="Não foi possível completar a requisição. Tente novamente mais tarde."
          onClose={() => setShowErrorBottomsheet(false)}
        />
      )}
    </div>
  )
}
